import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MutableRefObject,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
}

/**
 * Select — a fully custom, themed dropdown.
 *
 * The native <select> option popup can't be styled reliably (especially in dark
 * mode, where the OS renders a translucent/low-contrast list), so this renders
 * its own opaque, correctly-layered menu.
 *
 * A visually-hidden native <select> is kept in the DOM as the source of truth
 * for form libraries and refs: it holds the value/name, receives the ref, and
 * fires a real change event when the value updates — so every existing
 * `onChange={(e) => ... e.target.value}` consumer and react-hook-form
 * registration keeps working unchanged.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      error,
      hint,
      options,
      placeholder,
      id,
      value,
      onChange,
      onBlur,
      disabled,
      required,
      name,
      ...props
    },
    ref,
  ) => {
    const selectId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const errorId = error ? `${selectId}-error` : undefined;
    const hintId = hint && !error ? `${selectId}-hint` : undefined;
    const resolvedTestId = selectId ? `select-${selectId}` : undefined;
    const listboxId = useId();

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const nativeRef = useRef<HTMLSelectElement | null>(null);

    // Fixed-position coordinates for the portal-rendered menu. Rendering the
    // menu in a portal (instead of absolutely inside the field) prevents it
    // from being clipped by scrollable/overflow-hidden ancestors such as a
    // modal body.
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(
      null,
    );

    const updateMenuPosition = useCallback(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }, []);

    // Merge the forwarded ref with our internal ref to the native select.
    const setRefs = (el: HTMLSelectElement | null) => {
      nativeRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as MutableRefObject<HTMLSelectElement | null>).current = el;
    };

    const currentValue = value !== undefined && value !== null ? String(value) : '';
    const selectedOption = useMemo(
      () => options.find((o) => o.value === currentValue),
      [options, currentValue],
    );
    const displayLabel = selectedOption?.label ?? '';

    // Show a search box for longer lists so users can type to filter.
    const searchable = options.length > 7;
    const filteredOptions = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return options;
      return options.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(q));
    }, [options, search]);

    // Position the menu the moment it opens (before paint, to avoid a flash).
    useLayoutEffect(() => {
      if (open) updateMenuPosition();
    }, [open, updateMenuPosition]);

    // Reset the search box whenever the menu closes; focus it when it opens.
    useEffect(() => {
      if (open) {
        // Focus the search input shortly after the portal renders.
        const t = setTimeout(() => searchRef.current?.focus(), 0);
        return () => clearTimeout(t);
      }
      setSearch('');
      return undefined;
    }, [open]);

    // Close on outside click / Escape; keep the menu aligned on scroll/resize.
    useEffect(() => {
      if (!open) return;
      const onDocClick = (e: MouseEvent) => {
        const target = e.target as Node;
        const insideField = containerRef.current?.contains(target);
        const insideMenu = menuRef.current?.contains(target);
        if (!insideField && !insideMenu) {
          setOpen(false);
        }
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      };
      // Reposition on scroll (capture: catches scrolling ancestors like a modal
      // body) and on resize so the menu stays anchored to the trigger.
      const onReposition = () => updateMenuPosition();
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
        document.removeEventListener('mousedown', onDocClick);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('scroll', onReposition, true);
        window.removeEventListener('resize', onReposition);
      };
    }, [open, updateMenuPosition]);

    /**
     * Commits a new value through the native <select> so a real change event
     * fires — preserving the exact contract every consumer relies on.
     */
    const commitValue = (next: string) => {
      const el = nativeRef.current;
      if (el) {
        const setter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value',
        )?.set;
        setter?.call(el, next);
        el.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (onChange) {
        // Fallback: synthesize an event-like object.
        onChange({ target: { value: next, name } } as unknown as ChangeEvent<HTMLSelectElement>);
      }
      setOpen(false);
    };

    return (
      <div className="w-full" data-testid={resolvedTestId ? `field-${selectId}` : undefined}>
        {label && (
          <label
            htmlFor={selectId}
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {label}
            {required && (
              <span className="ml-0.5 text-danger-500" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        <div ref={containerRef} className="relative">
          {/* Visually-hidden native select: source of truth for value/ref/form. */}
          <select
            ref={setRefs}
            id={selectId}
            data-testid={resolvedTestId}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            value={currentValue}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            name={name}
            {...props}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Styled trigger button. */}
          <button
            ref={triggerRef}
            type="button"
            disabled={disabled}
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={errorId || hintId}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-left text-sm text-gray-900',
              'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0',
              'disabled:cursor-not-allowed disabled:bg-surface-secondary disabled:opacity-60',
              'dark:bg-surface-dark-secondary dark:text-gray-100',
              error
                ? 'border-danger-500 focus:ring-danger-500'
                : 'border-border focus:border-brand-500 focus:ring-brand-500 dark:border-border-dark',
              className,
            )}
          >
            <span className={cn('truncate', !displayLabel && 'text-gray-400 dark:text-gray-500')}>
              {displayLabel || placeholder || 'Select...'}
            </span>
            <svg
              className={cn(
                'h-4 w-4 shrink-0 text-gray-400 transition-transform',
                open && 'rotate-180',
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Custom opaque, themed option list — rendered in a portal with
              fixed positioning so it is never clipped by an overflow/scroll
              ancestor (e.g. a modal body) and always layers above it. */}
          {open &&
            !disabled &&
            menuPos &&
            createPortal(
              <ul
                ref={menuRef}
                id={listboxId}
                role="listbox"
                style={{
                  position: 'fixed',
                  top: menuPos.top + 4,
                  left: menuPos.left,
                  width: menuPos.width,
                }}
                className={cn(
                  'z-[100] mt-0 max-h-72 overflow-auto rounded-lg border py-1 shadow-lg',
                  'border-border bg-white dark:border-border-dark dark:bg-surface-dark-secondary',
                )}
              >
                {searchable && (
                  <li className="sticky top-0 z-10 border-b border-border bg-white p-2 dark:border-border-dark dark:bg-surface-dark-secondary">
                    <input
                      ref={searchRef}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter selects the only remaining match.
                        if (e.key === 'Enter' && filteredOptions.length === 1) {
                          e.preventDefault();
                          const only = filteredOptions[0];
                          if (!only.disabled) commitValue(only.value);
                        }
                      }}
                      placeholder="Search..."
                      className={cn(
                        'w-full rounded-md border px-2.5 py-1.5 text-sm',
                        'border-border bg-white text-gray-900 placeholder:text-gray-400',
                        'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                        'dark:border-border-dark dark:bg-surface-dark dark:text-gray-100',
                      )}
                    />
                  </li>
                )}
                {placeholder && !search.trim() && (
                  <SelectItem
                    label={placeholder}
                    selected={currentValue === ''}
                    muted
                    onSelect={() => commitValue('')}
                  />
                )}
                {filteredOptions.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matches</li>
                ) : (
                  filteredOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      label={option.label}
                      selected={option.value === currentValue}
                      disabled={option.disabled}
                      onSelect={() => !option.disabled && commitValue(option.value)}
                    />
                  ))
                )}
              </ul>,
              document.body,
            )}
        </div>

        {error && (
          <p id={errorId} className="mt-1.5 text-sm text-danger-500" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

/** A single option row in the custom dropdown menu. */
function SelectItem({
  label,
  selected,
  disabled,
  muted,
  onSelect,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  muted?: boolean;
  onSelect: () => void;
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        disabled={disabled}
        // Use onMouseDown so selection commits before the trigger's blur/outside handler.
        onMouseDown={(e) => {
          e.preventDefault();
          onSelect();
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
          'text-gray-900 dark:text-gray-100',
          'hover:bg-brand-50 dark:hover:bg-brand-900/20',
          selected && 'bg-brand-50 font-medium dark:bg-brand-900/30',
          muted && !selected && 'text-gray-500 dark:text-gray-400',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span className="truncate">{label}</span>
        {selected && (
          <svg
            className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
    </li>
  );
}
